import React, { createContext, useContext } from 'react';

// ─── Context splitting ─────────────────────────────────────────────────────────
// Two separate contexts to avoid re-rendering all consumers on every state update.
// Actions (useCallback → stable refs) rarely change.
// State (messages, agents, etc.) changes on each update.

export const HomeChatActionsContext = createContext(null);
export const HomeChatStateContext   = createContext(null);

// ─── Default values ────────────────────────────────────────────────────────────

const defaultActions = {
  // agents
  fetchAgents: async () => [],
  selectAgent: () => {},
  clearAgent: () => {},
  getAgentById: () => null,
  defaultAgentForNewChat: null,
  defaultAgentIdForNewChat: null,

  // conversations
  fetchConversations: async () => [],
  selectConversation: () => {},
  clearConversationSelection: () => {},
  addConversation: () => {},
  createNewConversation: async () => {},
  deleteConversation: async () => {},
  setConversations: () => {},
  setSelectedConversationId: () => {},
  setConversationId: () => {},
  setLastConversationId: () => {},
  setIsLoadingConversations: () => {},

  // messages
  fetchMessages: async () => [],
  sendMessage: async () => {},
  cleanThread: () => {},
  setMessages: () => {},
  setAgentIsTyping: () => {},
  setIsLoadingMessages: () => {},
  setError: () => {},

  // document panel
  openDocumentPanel: () => {},
  closeDocumentPanel: () => {},
  consumeLastOpenedFromMessageId: () => null,
  setSidebarCollapsed: () => {},
  addDocument: () => '',
  updateDocument: () => {},
  deleteDocument: () => {},
  getDocument: () => null,
  setCurrentDocument: () => {},
  clearCurrentDocument: () => {},
  searchDocuments: () => [],
  removeDocumentCreatedMessageId: () => {},

  // model selector
  onModelChange: () => {},
  onProviderChange: () => {},

  // conversation search
  openConversationSearch: () => {},
  closeConversationSearch: () => {},

  // slide-out
  setIsSlideOutMode: () => {},

  // global chat trigger
  openGlobalChat: () => {},
  closeGlobalChat: () => {},
  dismissGlobalChat: () => {},
  registerChatHost: () => {},
  unregisterChatHost: () => {},

  // context injection (Story 7)
  pushContext: () => {},
  removeContext: () => {},
  clearContext: () => {},
  syncContextEntry: () => {},

  // ui placeholders
  setAttachment: () => {},
  setPersistentFileUuids: () => {},

  // streaming (25+ event types)
  setCurrentActivity: () => {},
  setToolCalls: () => {},
  setHitlRequest: () => {},
  setSources: () => {},
  setUiBlocks: () => {},
  setMessageStatus: () => {},
  setPlanSteps: () => {},
  setIsStreaming: () => {},
};

const defaultState = {
  // agents
  agents: [],
  isLoadingAgents: false,
  isLoadingAgentDetails: false,
  agentsError: null,
  selectedAgent: null,
  selectedAgentId: null,
  conversationStarters: [],
  defaultAgentForNewChat: null,
  defaultAgentIdForNewChat: null,
  activeScope: null,

  // conversations
  conversations: [],
  selectedConversationId: null,
  conversationId: null,
  isLoadingConversations: false,
  isCreatingConversation: false,
  isDeletingConversation: false,

  // auth
  userId: null,
  tenantId: null,

  // messages
  messages: [],
  agentIsTyping: false,
  isLoadingMessages: false,
  error: null,

  // document panel
  isDocumentPanelOpen: false,
  documentPanelTitle: 'Document',
  documentPanelContent: '',
  isSidebarCollapsed: false,
  documents: [],
  currentDocumentId: null,
  currentDocument: null,
  sortedDocuments: [],
  documentCount: 0,
  documentCreatedMessageIdsByConversation: {},

  // model selector
  providers: [],
  selectedProvider: null,
  selectedModel: null,

  // conversation search
  isConversationSearchOpen: false,

  // slide-out
  isSlideOutMode: false,

  // global chat trigger
  isGlobalChatOpen: false,

  // ui placeholders
  isChatQuestionsVisible: true,
  dagProgress: [],
  currentDagNode: null,
  isChatReasoningActive: false,
  attachment: null,
  persistentFileUuids: [],

  // streaming state (25+ event types)
  currentActivity: null,
  toolCalls: [],
  hitlRequest: null,
  sources: [],
  uiBlocks: [],
  messageStatus: null,
  planSteps: [],
  isStreaming: false,
};

// ─── Provider component ────────────────────────────────────────────────────────

/**
 * Base Context Provider. Receives split values from HomeChatProviderWrapper.
 * Do not use directly; use HomeChatProviderWrapper instead.
 */
export const HomeChatProvider = ({ children, actionsValue, stateValue }) => {
  if (!actionsValue || !stateValue) {
    throw new Error(
      'HomeChatProvider requires actionsValue and stateValue props. Use HomeChatProviderWrapper instead.'
    );
  }

  return (
    <HomeChatActionsContext.Provider value={actionsValue}>
      <HomeChatStateContext.Provider value={stateValue}>
        {children}
      </HomeChatStateContext.Provider>
    </HomeChatActionsContext.Provider>
  );
};

// ─── Consumer hooks ────────────────────────────────────────────────────────────

/**
 * Hook for components that only consume actions (stable refs → minimal re-renders).
 */
export const useHomeChatActions = () => useContext(HomeChatActionsContext) ?? defaultActions;

/**
 * Hook for components that only consume state (re-renders on every state update).
 */
export const useHomeChatState = () => useContext(HomeChatStateContext) ?? defaultState;

/**
 * Backward-compatible hook — merges both contexts.
 * When used outside the Provider, returns the combined default value (avoids crash).
 */
export const useHomeChat = () => {
  const actions = useContext(HomeChatActionsContext) ?? defaultActions;
  const state = useContext(HomeChatStateContext) ?? defaultState;

  if (process.env.NODE_ENV === 'development') {
    const stateKeys = Object.keys(state);
    const actionKeys = Object.keys(actions);
    const overlap = stateKeys.filter((k) => actionKeys.includes(k));
    if (overlap.length > 0) {
      console.warn(
        `[useHomeChat] Key overlap detected between state and actions: ${overlap.join(', ')}. ` +
          `Actions will overwrite state for these keys.`
      );
    }
  }

  return { ...state, ...actions };
};

// Keep default export for imports that still use `import HomeChatContext from ...`
const HomeChatContext = HomeChatStateContext;
export default HomeChatContext;
